import React from "react";
import { ActionSheetIOS, ScrollView, Text, View } from "react-native";
import { Stack, useRouter } from "expo-router";
import { Feather } from "@expo/vector-icons";
import { observer } from "mobx-react-lite";

import { useAccountActions } from "~/components/AccountActions/context";
import {
  StackHeaderBack,
  StackHeaderRight,
  StackHeaderTitle,
} from "~/components/HeaderElements";
import { backlogSlug } from "~/app/(app)/(authenticated)/[account]/(account)/backlog";
import Logo from "~/assets/logo.svg";
import { useCurrentSpace } from "~/hooks/useCurrentSpace";
import { icons, isIOS, isWebDesktop } from "~/lib/constants";
import { AccountType, Space } from "~/stores/space/store";
import { Dropdown } from "~/ui/Dropdown";
import { ListContainer, ListItem } from "~/ui/List";

function SpaceIndexPage() {
  const { account, space } = useCurrentSpace();
  const { replace } = useRouter();
  const accountActions = useAccountActions();
  React.useEffect(() => {
    if (!isWebDesktop()) return;

    replace({
      pathname: "/(app)/(authenticated)/[account]/(space)/[space]/saves",
      params: {
        account: account.slug,
        space: space.slug,
      },
    });
  }, [account.slug, replace, space.slug]);

  return (
    <>
      <Stack.Screen
        options={{
          headerLeft: () => <StackHeaderBack />,
          headerTitle: () => (
            <StackHeaderTitle>
              {space.displayName ?? space.slug}
            </StackHeaderTitle>
          ),
          headerRight: () => (
            <StackHeaderRight>
              <HeaderMenu space={space} account={account} />
            </StackHeaderRight>
          ),
        }}
      />
      <ScrollView>
        <View className="gap-y-8 px-2 py-2">
          <ListContainer>
            <ListItem
              href={{
                pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
                params: {
                  account: account.slug,
                  space: space.slug,
                },
              }}
            >
              <View className="flex-row items-center">
                <Logo width={24} height={24} />
                <Text className="text-base text-white">All Saves</Text>
              </View>
              <Text className="text-text-secondary text-base">
                {space.savesCount}
              </Text>
            </ListItem>
          </ListContainer>

          {space.collections.size > 0 && (
            <ListContainer title="Collections">
              {space.collectionsAsArray.map((collection) => {
                const saveCount = space.savesAsArray.filter((save) =>
                  save.collections.has(collection.id),
                ).length;

                return (
                  <ListItem
                    key={collection.id}
                    href={{
                      pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
                      params: {
                        account: account.slug,
                        space: space.slug,
                        collectionId: collection.id,
                      },
                    }}
                    onLongPress={() => {
                      if (isIOS) {
                        return ActionSheetIOS.showActionSheetWithOptions(
                          {
                            options: ["Edit", "Delete", "Cancel"],
                            destructiveButtonIndex: 1,
                            cancelButtonIndex: 2,
                          },
                          (buttonIndex) => {
                            if (buttonIndex === 0) {
                              accountActions.openEditCollection({
                                collection,
                                space,
                              });
                            }
                            if (buttonIndex === 1) {
                              alert("delete the collection");
                            }
                          },
                        );
                      }

                      accountActions.openEditCollection({
                        collection,
                        space,
                      });
                    }}
                  >
                    <View className="flex-row items-center">
                      <Logo width={24} height={24} />
                      <Text className="text-base text-white">
                        {collection.title}
                      </Text>
                    </View>
                    <Text className="text-text-secondary text-base">
                      {saveCount}
                    </Text>
                  </ListItem>
                );
              })}
            </ListContainer>
          )}

          {/* <ListContainer title="Tags">
            <ListItem>
              <View className="flex-row items-center">
                <Text className="text-base text-white"># Pantone</Text>
              </View>
              <Text className="text-text-secondary text-base">15</Text>
            </ListItem>

            <ListItem>
              <View className="flex-row items-center">
                <Text className="text-base text-white"># Sans serif</Text>
              </View>
              <Text className="text-text-secondary text-base">24</Text>
            </ListItem>

            <ListItem>
              <View className="flex-row items-center">
                <Text className="text-base text-white"># Design system</Text>
              </View>
              <Text className="text-text-secondary text-base">12</Text>
            </ListItem>
          </ListContainer> */}
        </View>
      </ScrollView>
    </>
  );
}

function HeaderMenu(props: { account: AccountType; space: Space }) {
  const { account, space } = props;

  const { push } = useRouter();

  return (
    <>
      <Dropdown
        trigger={
          <Text className="text-text-primary">
            <Feather name="more-horizontal" size={24} />
          </Text>
        }
        groups={[
          {
            items: (
              [
                {
                  key: "Edit",
                  title: "Edit",
                  icon: icons.edit,
                  onSelect: () => {
                    push({
                      pathname: "/(app)/(authenticated)/[account]/spaceForm",
                      params: {
                        account: account.slug,
                        spaceId: space?.id,
                      },
                    });
                  },
                },

                {
                  key: "newCollection",
                  title: "New collection",
                  icon: icons.collection,
                  onSelect: () => {
                    push({
                      pathname:
                        "/(app)/(authenticated)/[account]/collection/form",
                      params: {
                        account: account.slug,
                        spaceId: space?.id,
                      },
                    });
                  },
                },

                // {
                //   key: "Share",
                //   title: "Share",
                //   icon: {
                //     ios: "square.and.arrow.up",
                //     web: "share",
                //   },
                // },
                {
                  key: "Delete",
                  title: "Delete",
                  icon: icons.delete,

                  destructive: true,
                },
              ] as const
            ).filter((item) => {
              if (space.slug === backlogSlug && item.key === "newCollection")
                return false;
              return true;
            }),
          },
        ]}
      />
    </>
  );
}

export default observer(SpaceIndexPage);
